import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  Checkbox,
  Collapsible,
  CollapsibleContent,
} from '@roger/ui-components';
import type { SelectableNestedTableRowModel } from './bespokeTemplatesImport';
import { triStateFromSelected } from './bespokeTemplatesImport';

const EXPAND_COL_CLASS =
  'flex h-6 w-6 shrink-0 items-center justify-center pointer-events-none';

type SelectableNestedTableSharedProps = {
  rows: SelectableNestedTableRowModel[];
  disabled?: boolean;
  /** Merged onto the bordered table wrapper (e.g. max height / scroll). */
  className?: string;
};

export type SelectableNestedTableProps =
  | (SelectableNestedTableSharedProps & {
      selectedKeys: ReadonlySet<string>;
      setSelectedKeys: React.Dispatch<React.SetStateAction<Set<string>>>;
      renderAccessory?: never;
    })
  | (SelectableNestedTableSharedProps & {
      selectedKeys?: never;
      setSelectedKeys?: never;
      /** Read-only mode: render this in the checkbox slot instead of a checkbox. */
      renderAccessory: (node: SelectableNestedTableRowModel) => React.ReactNode;
    });

function applyBranchKeys(
  setSelectedKeys: React.Dispatch<React.SetStateAction<Set<string>>>,
  keys: readonly string[],
  checked: boolean
) {
  setSelectedKeys(prev => {
    const next = new Set(prev);
    for (const k of keys) {
      if (checked) next.add(k);
      else next.delete(k);
    }
    return next;
  });
}

/** Single-line rows (fixed height band) for aligned table-like density. */
function RowCaption({ node }: { node: SelectableNestedTableRowModel }) {
  const hasChildren = Boolean(node.children?.length);
  const keyCount = node.branchLeafKeys.length;
  const primaryWeight = node.primaryBold ? 'font-semibold' : 'font-normal';
  const primaryTone =
    hasChildren || node.primaryBold
      ? 'text-foreground'
      : 'text-muted-foreground';

  const primaryTextClass = `truncate text-sm leading-none ${primaryTone} ${primaryWeight}`;

  return (
    <div className='flex min-h-7 min-w-0 flex-1 items-center gap-2 overflow-hidden'>
      {node.categoryLine ? (
        <span className='shrink-0 text-[10px] font-normal uppercase tracking-wide text-muted-foreground'>
          {node.categoryLine}
        </span>
      ) : null}
      <div className='flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden'>
        <span className={primaryTextClass}>{node.primaryLine}</span>
        {node.primaryMeta ? (
          <span className='shrink-0 truncate text-xs text-muted-foreground'>
            ({node.primaryMeta})
          </span>
        ) : null}
      </div>
      {hasChildren && keyCount > 0 ? (
        <span className='shrink-0 text-xs font-normal tabular-nums text-muted-foreground'>
          ({keyCount})
        </span>
      ) : null}
    </div>
  );
}

const CHECKBOX_SLOP_ATTR = 'data-selectable-tree-checkbox-slot' as const;

function CheckboxIsolationSlot(props: { children: React.ReactNode }) {
  return (
    <span
      {...{ [CHECKBOX_SLOP_ATTR]: '' }}
      className='inline-flex shrink-0 items-center justify-center self-center rounded-sm'
      onClick={event => event.stopPropagation()}
      onPointerDown={event => event.stopPropagation()}
    >
      {props.children}
    </span>
  );
}

function isFromCheckboxSlot(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    Boolean(target.closest(`[${CHECKBOX_SLOP_ATTR}]`))
  );
}

function NestedRow(props: {
  node: SelectableNestedTableRowModel;
  depth: number;
  selectedKeys?: ReadonlySet<string>;
  setSelectedKeys?: React.Dispatch<React.SetStateAction<Set<string>>>;
  disabled: boolean;
  renderAccessory?: (node: SelectableNestedTableRowModel) => React.ReactNode;
}) {
  const {
    node,
    depth,
    selectedKeys,
    setSelectedKeys,
    disabled,
    renderAccessory,
  } = props;

  const hasChildren = Boolean(node.children?.length);
  const checkboxId = `nested-sel-${node.id}`;
  const padLeftPx = Math.min(10 + depth * 22, 132);
  const [branchExpanded, setBranchExpanded] = useState(false);

  const rowBarClass =
    'flex min-h-8 items-center gap-2 border-b border-border bg-background py-1 pr-3 transition-colors hover:bg-muted/35';
  const branchRowInteractClass =
    'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

  if (!hasChildren) {
    const leafKey = node.branchLeafKeys[0];
    return (
      <div className={rowBarClass} style={{ paddingLeft: padLeftPx }}>
        <div className={EXPAND_COL_CLASS} aria-hidden />
        <CheckboxIsolationSlot>
          {renderAccessory ? (
            renderAccessory(node)
          ) : (
            <Checkbox
              id={checkboxId}
              className='shrink-0'
              aria-label={`Select "${node.primaryLine}"`}
              checked={selectedKeys?.has(leafKey) ?? false}
              disabled={disabled}
              onCheckedChange={v => {
                const on = v === true;
                if (setSelectedKeys) {
                  applyBranchKeys(setSelectedKeys, [leafKey], on);
                }
              }}
            />
          )}
        </CheckboxIsolationSlot>
        <div className='flex min-w-0 flex-1 items-center'>
          <RowCaption node={node} />
        </div>
      </div>
    );
  }

  const tri =
    selectedKeys != null
      ? triStateFromSelected([...node.branchLeafKeys], selectedKeys)
      : false;

  const selectionLabelParts = [
    node.categoryLine,
    node.primaryLine,
    `${node.branchLeafKeys.length} template(s)`,
  ].filter(Boolean);

  return (
    <Collapsible open={branchExpanded} onOpenChange={setBranchExpanded}>
      <div
        role='button'
        tabIndex={disabled ? undefined : 0}
        aria-expanded={branchExpanded}
        className={`${rowBarClass} ${branchRowInteractClass}`}
        style={{ paddingLeft: padLeftPx }}
        onKeyDown={event => {
          if (disabled) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setBranchExpanded(o => !o);
          }
        }}
        onClick={event => {
          if (disabled) return;
          if (isFromCheckboxSlot(event.target)) return;
          setBranchExpanded(o => !o);
        }}
      >
        <div
          className={`${EXPAND_COL_CLASS} text-muted-foreground`}
          aria-hidden
        >
          <ChevronRight
            className={`h-3.5 w-3.5 transition-transform duration-200 ${branchExpanded ? 'rotate-90' : ''}`}
          />
        </div>
        <CheckboxIsolationSlot>
          {renderAccessory ? (
            renderAccessory(node)
          ) : (
            <Checkbox
              id={checkboxId}
              className='shrink-0'
              aria-label={`Select rows: ${selectionLabelParts.join(' · ')}`}
              checked={tri}
              disabled={disabled}
              onCheckedChange={v => {
                const on = v === true;
                if (setSelectedKeys) {
                  applyBranchKeys(setSelectedKeys, node.branchLeafKeys, on);
                }
              }}
            />
          )}
        </CheckboxIsolationSlot>
        <div className='flex min-w-0 flex-1 items-center'>
          <RowCaption node={node} />
        </div>
      </div>

      <CollapsibleContent className='overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down'>
        <div className='border-t border-border bg-muted/25'>
          {node.children!.map(child => (
            <NestedRow
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              disabled={disabled}
              renderAccessory={renderAccessory}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Divider-lined, collapsible hierarchical rows where each checkbox applies to its
 * row’s `branchLeafKeys` (typically that row plus all descendant leaves).
 * Rows with children collapse when the row (outside the checkbox) is clicked.
 * Uses `@roger/ui-components` **`Collapsible`** (Radix).
 *
 * Modes are exclusive: selection (`selectedKeys` + `setSelectedKeys`) or
 * accessory (`renderAccessory`).
 */
export function SelectableNestedTable(props: SelectableNestedTableProps) {
  const { rows, disabled = false, className = '' } = props;
  const selectedKeys = 'selectedKeys' in props ? props.selectedKeys : undefined;
  const setSelectedKeys =
    'setSelectedKeys' in props ? props.setSelectedKeys : undefined;
  const renderAccessory =
    'renderAccessory' in props ? props.renderAccessory : undefined;

  return (
    <div
      className={`overflow-hidden rounded-md border border-border bg-card text-card-foreground${className ? ` ${className}` : ''}`}
    >
      {rows.map(row => (
        <NestedRow
          key={row.id}
          node={row}
          depth={0}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          disabled={disabled}
          renderAccessory={renderAccessory}
        />
      ))}
    </div>
  );
}
